import React from 'react';
import { Eye } from 'lucide-react';
import Button from '@components/common/Button';
import ContractManagerCell from './ContractManagerCell';
import DivisionCell from './DivisionCell';
import InProgressCell from './InProgressCell';
import ContractOwnerCell from './ContractOwnerCell';
import StateCell from './StateCell';
import ContractNumberCell from './ContractNumberCell';
import SupplierCell from './SupplierCell';
import TierCell from './TierCell';
import UrgencyCell from './UrgencyCell';
import TargetDateCell from './TargetDateCell';
import AssessorCell from './AssessorCell';
import MeetingDateCell from './MeetingDateCell';
import { ScheduleItem, TableColumn, ScheduleItemContract } from '@app-types';
import { UserPermissions } from '@contexts/AuthContext';
import { formatMediumCalendarDate } from '@utils/dateUtils';
import { formatCurrency } from '@utils/currencyUtils';
import styles from './SchedulerTableCells.module.css';

// Main Cell Renderer
export const SchedulerCellRenderer = ({
  column,
  scheduleItem,
  contract,
  isGroupRow = false,
  isChildRow = false,
  permissions,
  onAssignRole,
  onScheduleDate,
  canScheduleMeeting,
  navigate,
  isSelected,
  isExpanded,
  onToggleSelection,
  onToggleExpand,
  onAddToGroup,
  onRemoveFromGroup,
  isCheckboxDisabled
}: {
  column: TableColumn;
  scheduleItem: ScheduleItem;
  contract: ScheduleItemContract;
  isGroupRow?: boolean;
  isChildRow?: boolean;
  permissions: UserPermissions;
  onAssignRole: (
    item: ScheduleItem,
    contractId: number,
    role: 'owner' | 'manager' | 'assessor',
    isGroupOperation?: boolean
  ) => void;
  onScheduleDate: (item: ScheduleItem) => void;
  canScheduleMeeting: (item: ScheduleItem) => boolean;
  navigate: (path: string) => void;
  isSelected?: boolean;
  isExpanded?: boolean;
  onToggleSelection?: (itemId: number | null, contractId: number, item?: ScheduleItem) => void;
  onToggleExpand?: (itemId: number) => void;
  onAddToGroup?: (item: ScheduleItem) => void;
  onRemoveFromGroup?: (groupId: number, contractId: number) => void;
  isCheckboxDisabled?: boolean;
}): React.ReactElement => {
  // For collapsed group rows, only show specific columns
  const allowedGroupColumns = [
    'state',
    'tier',
    'assessor',
    'contract_manager',
    'contract_owner',
    'meeting_date',
    'division',
    'in_progress',
    'view'
  ];

  if (isGroupRow && !isChildRow && !allowedGroupColumns.includes(column.key)) {
    // For urgency column, use centered empty cell; for others, use left-aligned
    if (column.key === 'urgency') {
      return <div className={styles.emptyCellCentered}>—</div>;
    }
    return <span className={styles.emptyCell}>—</span>;
  }

  switch (column.key) {
    case 'contract_number':
      return (
        <ContractNumberCell
          scheduleItem={scheduleItem}
          contract={contract}
          isGroupRow={isGroupRow}
          isChildRow={isChildRow}
          navigate={navigate}
        />
      );

    case 'supplier':
      return (
        <SupplierCell
          scheduleItem={scheduleItem}
          contract={contract}
          isGroupRow={isGroupRow}
          isChildRow={isChildRow}
          permissions={permissions}
          navigate={navigate}
        />
      );

    case 'tier':
      return (
        <TierCell tier={contract.tier} scheduleItem={scheduleItem} isGroupRow={isGroupRow} isChildRow={isChildRow} />
      );

    case 'division':
      return (
        <DivisionCell scheduleItem={scheduleItem} contract={contract} isGroupRow={isGroupRow} isChildRow={isChildRow} />
      );

    case 'urgency':
      return (
        <UrgencyCell
          urgency={contract.urgency}
          targetDate={contract.target_date}
          isGroupRow={isGroupRow}
          isChildRow={isChildRow}
        />
      );

    case 'contract_manager':
      return (
        <ContractManagerCell
          scheduleItem={scheduleItem}
          contract={contract}
          isGroupRow={isGroupRow}
          isChildRow={isChildRow}
          permissions={permissions}
          onAssignRole={onAssignRole}
        />
      );

    case 'contract_owner':
      return (
        <ContractOwnerCell
          scheduleItem={scheduleItem}
          contract={contract}
          isGroupRow={isGroupRow}
          isChildRow={isChildRow}
          permissions={permissions}
          onAssignRole={onAssignRole}
        />
      );

    case 'assessor':
      return (
        <AssessorCell
          scheduleItem={scheduleItem}
          permissions={permissions}
          onAssignRole={onAssignRole}
          isChildRow={isChildRow}
        />
      );

    case 'target_date':
      return <TargetDateCell urgency={contract.urgency} targetDate={contract.target_date} />;

    case 'meeting_date':
      return (
        <MeetingDateCell
          scheduleItem={scheduleItem}
          onScheduleDate={onScheduleDate}
          canScheduleMeeting={canScheduleMeeting}
          isChildRow={isChildRow}
          isGroupRow={isGroupRow}
        />
      );

    case 'state':
      return (
        <StateCell
          scheduleItem={scheduleItem}
          contract={contract}
          isGroupRow={isGroupRow}
          isChildRow={isChildRow}
          isCheckboxDisabled={isCheckboxDisabled}
          isSelected={isSelected}
          isExpanded={isExpanded}
          onToggleSelection={onToggleSelection}
          onToggleExpand={onToggleExpand}
          onAddToGroup={onAddToGroup}
          onRemoveFromGroup={onRemoveFromGroup}
        />
      );

    case 'in_progress':
      return <InProgressCell scheduleItem={scheduleItem} isGroupRow={isGroupRow} isChildRow={isChildRow} />;

    case 'name':
      return <span>{contract.name || '-'}</span>;

    case 'start_date':
      return <span>{formatMediumCalendarDate(contract.start_date)}</span>;

    case 'end_date':
      return <span>{formatMediumCalendarDate(contract.end_date)}</span>;

    case 'amount':
      return <span>{formatCurrency(contract.amount)}</span>;

    case 'contract_type':
      return <span>{contract.contract_type || '-'}</span>;

    case 'cost_center':
      return <span>{contract.cost_center || '-'}</span>;

    case 'scheduling_notes':
      return <span>{scheduleItem.meeting?.notes || '-'}</span>;

    case 'rescheduled_date':
      return <span>{formatMediumCalendarDate(scheduleItem.meeting?.previous_scheduled_datetime)}</span>;

    case 'rescheduled_reason':
      return <span>{scheduleItem.meeting?.reschedule_reason || '-'}</span>;

    case 'view':
      // Only show view button for parent row (group) or single row with a meeting ID
      if (isChildRow || !scheduleItem.id) {
        return <span></span>;
      }
      return (
        <div className={styles.actionsCell} title="View meeting details">
          <Button
            variant="ghost"
            size="xs"
            onClick={() => navigate(`/contract-assessment-meetings/${scheduleItem.id}`)}
            className={styles.viewMeetingBtn}
          >
            <Eye size={18} />
          </Button>
        </div>
      );

    default: {
      const value = contract[column.key as keyof ScheduleItemContract];
      return <span>{typeof value === 'object' ? '-' : value || '-'}</span>;
    }
  }
};
